import React from "react";
import styled from "styled-components";
import { Lock, KeyRound } from "lucide-react"; 

const StyledComp = styled.section`
  padding: 60px 40px;

  .header {
    display: grid;
    grid-template-columns: 1fr auto;
    gap: 48px;
    align-items: start;
    margin-bottom: 48px;
  }

  .eyebrow {
    font-size: 14px;
    color: var(--grey);
    margin-bottom: 12px;
  }

  h2 {
    font-size: 3rem;
    line-height: 1.05;
    letter-spacing: -0.04em;
    margin: 0;
    max-width: 700px;
  }

  .description {
    max-width: 420px;
    color: #6b7280;
    font-size: 1rem;
    line-height: 1.7;
  }

  .grid {
    display: grid;
    grid-template-columns: repeat(3, 1fr);
    gap: 24px;
  }

  .card {
    background: white;
    border-radius: 8px;
    padding: 32px;
    height: 420px;
    border: 1px solid #e5e7eb;
    box-shadow:
      0 1px 2px rgba(0,0,0,0.04),
      0 10px 30px rgba(0,0,0,0.04);

    overflow: hidden;
    display: flex;
    flex-direction column;
    flex-direction: column;
    justify-content: space-between;
  }

  .card h3 {
    margin: 0 0 12px;
    font-size: 1.4rem;
    font-weight: 700;
  }

  .card p {
    color: #6b7280;
    line-height: 1.7;
    margin-bottom: 28px;
  }

  .vault-grid {
    display: grid;
    grid-template-columns: repeat(7, 1fr);
    gap: 10px;
  }

  .vault-cell {
    aspect-ratio: 1;
    border-radius: 12px;
    border: 1px solid #e5e7eb;
    background: #fafafa;

    display: flex;
    align-items: center;
    justify-content: center;
  }

  .vault-cell.highlight {
    background: #fff7ed;
    border-color: #fdba74;
  }

  .vault-cell svg {
    width: 16px;
    height: 16px;
    color: #9ca3af;
  }

  .vault-cell.highlight svg {
    color: #f97316;
  }

  .apps-grid {
    display: grid;
    grid-template-columns: repeat(6, 1fr);
    gap: 12px;
  }

  .vault-grid,
  .apps-grid {
    position: relative;

    mask-image: radial-gradient(
      ellipse at center,
      rgba(0, 0, 0, 1) 60%,
      rgba(0, 0, 0, 0.85) 72%,
      rgba(0, 0, 0, 0.45) 85%,
      rgba(0, 0, 0, 0) 100%
    );

    -webkit-mask-image: radial-gradient(
      ellipse at center,
      rgba(0, 0, 0, 1) 60%,
      rgba(0, 0, 0, 0.85) 72%,
      rgba(0, 0, 0, 0.45) 85%,
      rgba(0, 0, 0, 0) 100%
    );
  }
  .app {
    aspect-ratio: 1;
    border-radius: 14px;
    border: 1px solid #e5e7eb;
    background: #fafafa;

    display: flex;
    align-items: center;
    justify-content: center;
  }

  .app img {
    width: 20px;
    height: 20px;
    object-fit: contain;
  }

  .sheet {
    margin-top: 12px;
    border-radius: 20px;
    overflow: hidden;
    border: 1px solid #e5e7eb;
  }

  .sheet-header {
    padding: 12px 16px;
    background: #f9fafb;
    border-bottom: 1px solid #e5e7eb;

    display: flex;
    gap: 8px;
    align-items: center;
    color: #6b7280;
    font-size: 14px;
  }

  .dot {
    width: 10px;
    height: 10px;
    border-radius: 999px;
    background: #d1d5db;
  }

  table {
    width: 100%;
    border-collapse: collapse;
    font-size: 14px;
  }

  th,
  td {
    border: 1px solid #f3f4f6;
    padding: 12px;
    text-align: left;
  }

  th {
    background: #fafafa;
    color: #6b7280;
    font-weight: 600;
  }

  .positive {
    background: #ecfdf5;
    color: #047857;
    font-weight: 600;
  }

  @media (max-width: 1024px) {
    .header,
    .grid {
      grid-template-columns: 1fr;
    }

    .description {
      max-width: 100%;
    }
  }
`;

const appIcons = [
  { src: "/integrations/github-icon.svg", alt: "GitHub" },
  { src: "/integrations/slack-icon.svg", alt: "Slack" },
  { src: "/integrations/excel-icon.svg", alt: "MS Excel" },
  { src: "/integrations/vercel-icon.svg", alt: "Vercel" },
  { src: "/integrations/notion-icon.svg", alt: "Notion" },
  { src: "/integrations/claude-icon.svg", alt: "Claude" },
  { src: "/integrations/sentry-icon.svg", alt: "Sentry" },
  { src: "/integrations/atlassian.svg", alt: "Atlassian" },
  { src: "/integrations/numpy.svg", alt: "NumPy" },
  { src: "/integrations/pytorch-icon.svg", alt: "PyTorch" },
  { src: "/integrations/supabase-icon.svg", alt: "Supabase" },
  { src: "/integrations/microsoft-onedrive.svg", alt: "OneDrive" },
  { src: "/integrations/model-context-protocol-icon.svg", alt: "MCP" },
  { src: "/integrations/sheets-icon.svg", alt: "Google Sheets" },
  { src: "/integrations/tensorflow.svg", alt: "TensorFlow" },
  { src: "/integrations/openai-icon.svg", alt: "OpenAI" },
  { src: "/integrations/airtable.svg", alt: "Airtable" },
  { src: "/integrations/microsoft-power-bi.svg", alt: "Power BI" },
  { src: "/integrations/sap.svg", alt: "SAP" },
  { src: "/integrations/pandas-icon.svg", alt: "Pandas" },
  { src: "/integrations/tableau-icon.svg", alt: "Tableau" },

  { src: "/integrations/gitlab-icon.svg", alt: "GitLab" },
  { src: "/integrations/aws.svg", alt: "AWS" },
  { src: "/integrations/deepseek-icon.svg", alt: "Deepseek" },
];

const Comp = () => {
  return (
    <StyledComp>
      <div className="container">
        <div className="header">
          <div>
            <div className="eyebrow">Features</div>

            <h2 className="serif">Enterprise-grade financial data SDK clients, ready to deploy</h2>
          </div>

          <div className="description">
            Secure data isolation, full historical depth across your
            tools, and production-ready TypeScript financial API clients for every team.
          </div>
        </div>

        <div className="grid">
          {/* Vault */}
          <div className="card">
            <div>
            <h3>Secure, Enterprise Vault</h3>

            <p>
              Connect using scoped API keys that are safe for both client and server contexts.
            </p>
            </div>

            <div className="vault-grid">
              {Array.from({ length: 35 }).map((_, i) => (
                <div
                  key={i}
                  className={`vault-cell ${
                    i === 17 ? "highlight" : ""
                  }`}
                >
                  {i === 17 ? <KeyRound /> : <Lock />}
                </div>
              ))}
            </div>
          </div>

          {/* Integrations */}
          <div className="card">
            <div>
            <h3>Deep historical search and knowledge</h3>

            <p>
              Connect your external applications and integrate financial intelligence across your entire stack with one financial data SDK.
            </p>
            </div>

            <div className="apps-grid">
              {appIcons.map((icon, index) => (
                <div className="app" key={index}>
                  <img src={icon.src} alt={icon.alt} width="20" height="20" loading="lazy" />
                </div>
              ))}
            </div>
          </div>

          {/* Spreadsheet */}
          <div className="card">
            <div>
            <h3>Slides, spreadsheets, and documents</h3>

            <p>
              Produce polished financial analytics in slides, spreadsheets, and documents.
            </p>
            </div>

            <div className="sheet">
              <div className="sheet-header">
                <div className="dot" />
                <div className="dot" />
                <div className="dot" />

                <span>forecast_model.xlsx</span>
              </div>

              <table>
                <thead>
                  <tr>
                    <th></th>
                    <th>A</th>
                    <th>B</th>
                    <th>C</th>
                  </tr>
                </thead>

                <tbody>
                  <tr>
                    <td>Revenue</td>
                    <td>$1.2M</td>
                    <td>$1.5M</td>
                    <td className="positive">$1.8M</td>
                  </tr>

                  <tr>
                    <td>COGS</td>
                    <td>$520K</td>
                    <td>$610K</td>
                    <td>$720K</td>
                  </tr>

                  <tr>
                    <td>OpEx</td>
                    <td>$180K</td>
                    <td>$210K</td>
                    <td>$240K</td>
                  </tr>

                  <tr>
                    <td>Gross Profit</td>
                    <td>$680K</td>
                    <td>$890K</td>
                    <td className="positive">$1.08M</td>
                  </tr>

                  {/* New rows */}

                  <tr>
                    <td>Marketing</td>
                    <td>$90K</td>
                    <td>$110K</td>
                    <td>$135K</td>
                  </tr>

                  <tr>
                    <td>R&amp;D</td>
                    <td>$120K</td>
                    <td>$145K</td>
                    <td>$170K</td>
                  </tr>

                  <tr>
                    <td>Headcount</td>
                    <td>42</td>
                    <td>48</td>
                    <td className="positive">56</td>
                  </tr>

                  <tr>
                    <td>EBITDA</td>
                    <td>$470K</td>
                    <td>$635K</td>
                    <td className="positive">$775K</td>
                  </tr>

                  <tr>
                    <td>Margin</td>
                    <td>39.2%</td>
                    <td>42.3%</td>
                    <td className="positive">43.1%</td>
                  </tr>
                </tbody>
              </table>
            </div>
          </div>
        </div>
      </div>
    </StyledComp>
  );
};

export default Comp;
